import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ExternalLink, Clock, Tag, Calendar } from 'lucide-react';

const MEDIUM_USERNAME = 'krishnasrivastava41357';
// NOTE: do NOT add &count= — rss2json requires a paid API key for that param
const RSS2JSON_URL = `https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@${MEDIUM_USERNAME}`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] },
});

/* Strip HTML tags from Medium content to get a plain-text excerpt */
function stripHtml(html = '') {
  return html.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, ' ').trim();
}

/* Estimate reading time from raw HTML content */
function readingTime(html = '') {
  const words = stripHtml(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

/* Format ISO date string → "Aug 2026" */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/* Extract first <img> src from HTML string — Medium leaves thumbnail empty */
function extractImageFromHtml(html = '') {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/);
  return match ? match[1] : null;
}

/* Skeleton card shown while loading */
function SkeletonCard() {
  return (
    <div className="bg-white border-2 border-black rounded-2xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-pulse">
      <div className="h-36 bg-black/8" />
      <div className="p-4 space-y-3">
        <div className="h-3 bg-black/10 rounded w-1/3" />
        <div className="h-5 bg-black/12 rounded w-5/6" />
        <div className="h-3 bg-black/8 rounded w-full" />
        <div className="h-3 bg-black/8 rounded w-3/4" />
      </div>
    </div>
  );
}

/* Individual blog post card */
function PostCard({ post, index }) {
  const mins = readingTime(post.content || post.description);
  const excerpt = stripHtml(post.description || post.content).slice(0, 120) + '…';
  const tags = (post.categories || []).slice(0, 3);
  // Medium's thumbnail field is often empty — fall back to first image in content
  const thumb = (post.thumbnail && post.thumbnail.length > 0)
    ? post.thumbnail
    : extractImageFromHtml(post.content || post.description);

  return (
    <motion.a
      {...fadeUp(0.1 + index * 0.07)}
      href={post.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col bg-white border-2 border-black rounded-2xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
    >
      {/* Thumbnail */}
      {thumb ? (
        <div className="h-36 overflow-hidden bg-black/5 shrink-0">
          <img
            src={thumb}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
          />
        </div>
      ) : (
        <div className="h-36 bg-gradient-to-br from-purple-50 to-purple-100 border-b-2 border-black flex items-center justify-center shrink-0">
          <BookOpen size={32} className="text-purple-300" />
        </div>
      )}

      {/* Card body */}
      <div className="p-4 flex flex-col flex-1 gap-2">
        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Tag size={9} className="text-purple-500 shrink-0" />
            {tags.map((t) => (
              <span
                key={t}
                className="text-[9px] font-mono uppercase tracking-wider text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-md"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="text-[13px] font-black text-black leading-snug line-clamp-2 group-hover:text-purple-600 transition-colors duration-150">
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="text-[11px] text-black/40 font-mono leading-relaxed line-clamp-2 flex-1">
          {excerpt}
        </p>

        {/* Meta row */}
        <div className="flex items-center justify-between pt-2 border-t border-black/8 mt-auto">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[10px] font-mono text-black/30">
              <Calendar size={9} />
              {formatDate(post.pubDate)}
            </span>
            <span className="flex items-center gap-1 text-[10px] font-mono text-black/30">
              <Clock size={9} />
              {mins} min read
            </span>
          </div>
          <ExternalLink
            size={11}
            className="text-black/20 group-hover:text-purple-500 transition-colors duration-150 shrink-0"
          />
        </div>
      </div>
    </motion.a>
  );
}

export default function Writings() {
  const [posts, setPosts]   = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'

  useEffect(() => {
    fetch(RSS2JSON_URL)
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'ok' && Array.isArray(data.items) && data.items.length > 0) {
          // rss2json free tier returns up to 10 items; we cap at 4
          setPosts(data.items.slice(0, 4));
          setStatus('ok');
        } else {
          setStatus('error');
        }
      })
      .catch(() => setStatus('error'));
  }, []);

  return (
    <section id="writings" className="relative w-full overflow-hidden bg-transparent py-8 lg:py-10">
      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 lg:px-14">

        {/* ── Header ── */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <motion.p {...fadeUp(0)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-1">
              &lt;/Writings&gt;
            </motion.p>
            <motion.h2 {...fadeUp(0.04)} className="text-2xl sm:text-3xl font-black text-black">
              From my blog
            </motion.h2>
            <motion.p {...fadeUp(0.07)} className="text-[11px] font-mono text-black/40 mt-1">
              Thoughts on tech, dev life &amp; things I&apos;m learning — on Medium
            </motion.p>
          </div>

          <motion.a
            {...fadeUp(0.08)}
            href={`https://medium.com/@${MEDIUM_USERNAME}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-black text-white text-[11px] font-black rounded-xl border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 whitespace-nowrap shrink-0"
          >
            <BookOpen size={11} />
            All posts
          </motion.a>
        </div>

        <motion.div {...fadeUp(0.09)} className="h-px bg-black/10 mb-6" />

        {/* ── States ── */}
        {status === 'loading' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {status === 'ok' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {posts.map((post, i) => (
              <PostCard key={post.guid || post.link} post={post} index={i} />
            ))}
          </div>
        )}

        {status === 'error' && (
          <motion.div
            {...fadeUp(0.1)}
            className="flex flex-col items-center justify-center gap-3 py-14 border-2 border-dashed border-black/15 rounded-2xl"
          >
            <BookOpen size={28} className="text-black/20" />
            <p className="text-[12px] font-mono text-black/40">Couldn&apos;t load posts right now.</p>
            <a
              href={`https://medium.com/@${MEDIUM_USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-black text-purple-600 hover:underline"
            >
              Visit Medium profile &rarr;
            </a>
          </motion.div>
        )}

      </div>
    </section>
  );
}
